import type { JSX } from 'preact';
import { resolveEdgeTab, resolveIcon, type EdgeTabShape } from '@feedback/shared';
import type { WidgetConfig } from '../api/types';
import { strings } from '../strings';
import { validFontStack, validHex } from './accent';
import { clampBorderRadius } from './brand';
import { darken, HOVER_DARKEN_AMOUNT } from './colors';
import { renderLauncherIcon } from './Button';

/** How much darker a pressed tab is than its fill. The button has no pressed shade to match. */
const PRESSED_DARKEN_AMOUNT = 0.24;

const DEFAULT_LABEL = 'Feedback';

export interface EdgeTabAppearance {
  /**
   * `--fb-*` custom properties for `.ft-root`, one per color the application
   * configured. A color it did not configure is left out, so the stylesheet's
   * own value, light or dark-host, still applies.
   */
  vars: Record<string, string>;
  label: string;
  tooltip: string | null;
  tab: EdgeTabShape;
  /** The chosen icon drawn in `currentColor`, or null for the tab's own glyph. */
  icon: JSX.Element | null;
  /** The resolved colors, for the `mountTrigger` button, which lives outside the shadow root. */
  fill: string | null;
  ink: string | null;
}

/**
 * The application's Appearance settings, resolved for the edge tab, the phone
 * dock and a docked trigger (I22656). Same derivations as `Button.tsx`: an
 * explicit hover color wins, else the fill is darkened; the icon follows the
 * hover text color only when one is configured.
 *
 * Colors pass through `validHex` because they are interpolated into custom
 * properties that the stylesheet reads, and a free-text value from the admin
 * form ("navy") would otherwise paint nothing at all.
 */
export function resolveEdgeTabAppearance(config: WidgetConfig): EdgeTabAppearance {
  const fill = validHex(config.bgColor);
  const ink = validHex(config.textColor);
  const iconInk = validHex(config.iconColor) ?? ink;
  const hoverInk = validHex(config.hoverTextColor);

  const vars: Record<string, string> = {};
  if (fill) {
    vars['--fb-fill'] = fill;
    vars['--fb-fill-hover'] = validHex(config.hoverBgColor) ?? darken(fill, HOVER_DARKEN_AMOUNT);
    vars['--fb-fill-pressed'] = darken(fill, PRESSED_DARKEN_AMOUNT);
  } else if (validHex(config.hoverBgColor)) {
    vars['--fb-fill-hover'] = validHex(config.hoverBgColor)!;
  }
  if (ink) vars['--fb-ink'] = ink;
  if (iconInk) vars['--fb-icon-ink'] = iconInk;
  if (hoverInk) {
    vars['--fb-ink-hover'] = hoverInk;
    vars['--fb-icon-ink-hover'] = hoverInk;
  }
  const font = validFontStack(config.fontFamily);
  if (font) vars['--fb-font'] = `600 12.5px/1 ${font}`;

  const tab = resolveEdgeTab(config.shape, {
    borderRadius: clampBorderRadius(config.borderRadius),
    maskUrl: config.shapeAssetUrl,
    buttonMode: config.buttonMode,
  });

  const icon = resolveIcon(config.iconUrl).kind === 'default' ? null : renderLauncherIcon(config.iconUrl, 'currentColor', 16);

  const tooltip =
    config.tooltipEnabled === false ? null : config.tooltipText?.trim() || strings.tooltip.defaultText;

  return {
    vars,
    label: config.label?.trim() ? config.label.trim() : DEFAULT_LABEL,
    tooltip,
    tab,
    icon,
    fill,
    ink,
  };
}
